import { Component, OnInit } from '@angular/core';
import { BFEvents, ModalSize, MultiLineComponent } from '@fgpp-ui/components';
import { Observable, map, of, take } from 'rxjs';
import { IDynamicModalDialogData } from '../../../core/interfaces/dynamic-modal-dialog-data.interface';
import { IValueChange } from '../../../core/interfaces/form-value-change.interface';
import { BFOnFormLoadComplete, BFOnValueChange, BFProfileOnSave } from '../../../core/interfaces/sdk-profile-hooks.interface';
import { ProfileFormTemplateProviders } from '../../../core/templates/profile-form.template.providers';
import { AbstractProfileFormComponent } from '../../abstract/abstract-profile/abstract-profile-form.component';
import { ProfileLayoutType } from '../../enums/profile-layout-type.enum';
import { Accounts } from '../../models/accounts.model';
import { FeeFormula } from '../../models/fee-formula.model';
import { FeeType } from '../../models/fee-type.model';
import { PayingPartyType } from '../fee-type/enums/paying-party-type.enum';
import { ApplyFee, ConfirmModalType, DeductFrom, FeeMethod } from './enums';
import { FeeFormulaConfirmModalContract, FeeFormulaConfirmModalLayout, FeeFormulaTierModalComponent } from './modals';


@Component({
    selector: 'app-fee-formula',
    templateUrl: '../../../core/templates/profile-form.template.html',
    styleUrls: ['../../../core/templates/profile-form.template.scss'],
    providers: ProfileFormTemplateProviders
})
export class FeeFormulaComponent extends AbstractProfileFormComponent implements BFOnFormLoadComplete, BFOnValueChange, BFProfileOnSave, OnInit {

    private applyFeeLastValue: ApplyFee;

    fgppFormLoadComplete(): void {
        this.handleFieldsByFeeType(false);
        this.handleFieldsByDeductFrom(false);
        this.handleFieldsByApplyFee(false);
        this.handleFieldsByFeeMethod(false);
        this.handleFieldsByFixedAmount(false);
        this.handleFieldsByPercentage(false);
        this.updateMaxFeeValidation();
        this.formMgr.get(FeeFormula.DETAILS).getLayoutControl<MultiLineComponent>().setModalComponent(FeeFormulaTierModalComponent);
    }

    fgppValueChanges(change: IValueChange): void {
        switch (change.control) {
            case FeeFormula.FEE_TYPE_NAME:
                this.handleFieldsByFeeType(true)
                break;
            case FeeFormula.DEDUCT_FROM:
                this.handleFieldsByDeductFrom(true)
                break;
            case FeeFormula.APPLY_FEE:
                this.handleFieldsByApplyFee(true)
                break;
            case FeeFormula.FEES_SUSP_ACC_NO:
                this.handleFieldsByAccountNumber();
                break;
            case FeeFormula.METHOD:
                this.handleFieldsByFeeMethod(true);
                break;
            case FeeFormula.FIXED_FEE:
                this.handleFieldsByFixedAmount(true);
                break;
            case FeeFormula.PERCENTAGE_FEE:
                this.handleFieldsByPercentage(true);
                break;
            case FeeFormula.MIN_FEE:
                this.updateMaxFeeValidation();
                break;
        }
    }

    fgppOnSave(): boolean {
        return this.validateMandatoryFixedAmountOrPercentage();
    }

    private handleFieldsByFeeType(feeTypeChanged: boolean): void {
        let partyType: string;
        if (feeTypeChanged) {
            const data = this.getDrilldownData(FeeFormula.FEE_TYPE_NAME);
            partyType = data ? data[FeeType.PAYING_PARTY.replace('-', '.')] : '';
        } else {
            partyType = this.formMgr.get(FeeFormula.V_FEE_PARTYTYPE).getValue();
        }
        if (partyType === PayingPartyType.AGENT_FEE) {
            if (feeTypeChanged) {
                this.formMgr.get(FeeFormula.DEDUCT_FROM).setValue(DeductFrom.ACCOUNT);
                this.formMgr.get(FeeFormula.APPLY_FEE).setValue(ApplyFee.NOW);
                this.formMgr.get(FeeFormula.FEES_SUSP_ACC_NO).markAsUntouched().setValue('');
                this.formMgr.get(FeeFormula.FEES_SUSP_ACC_CCY).markAsUntouched().setValue('');
                this.formMgr.get(FeeFormula.FEES_SUSP_ACC_OFFICE).markAsUntouched().setValue('');
            }
            this.formMgr.get(FeeFormula.DEDUCT_FROM).disable();
            this.formMgr.get(FeeFormula.APPLY_FEE).disable();
            this.formMgr.get(FeeFormula.FEES_SUSP_ACC_NO).disable();
        } else if (feeTypeChanged && this.formMgr.get(FeeFormula.V_FEE_PARTYTYPE).getValue() === PayingPartyType.AGENT_FEE) {
            this.formMgr.get(FeeFormula.DEDUCT_FROM).markAsUntouched().setValue('').enable();
            this.formMgr.get(FeeFormula.APPLY_FEE).enable();
            this.formMgr.get(FeeFormula.FEES_SUSP_ACC_NO).markAsUntouched().setValue('').enable();
            this.formMgr.get(FeeFormula.FEES_SUSP_ACC_CCY).markAsUntouched().setValue('');
            this.formMgr.get(FeeFormula.FEES_SUSP_ACC_OFFICE).markAsUntouched().setValue('');
        }
        this.formMgr.get(FeeFormula.V_FEE_PARTYTYPE).setValue(partyType);
    }

    private handleFieldsByDeductFrom(deductFromChanged: boolean): void {
        const deductFrom = this.formMgr.get(FeeFormula.DEDUCT_FROM);
        const applyFee = this.formMgr.get(FeeFormula.APPLY_FEE);

        if (deductFrom.getValue() === DeductFrom.PAYMENT) {
            if (deductFromChanged) {
                applyFee.setValue(ApplyFee.NOW);
            }
            applyFee.disable();
        } else if (this.formMgr.get(FeeFormula.V_FEE_PARTYTYPE).getValue() !== PayingPartyType.AGENT_FEE && this.layoutMode != ProfileLayoutType.VIEW) {
            applyFee.enable();
        }
    }

    private handleFieldsByApplyFee(applyFeeChanged: boolean): void {
        const applyFee = this.formMgr.get(FeeFormula.APPLY_FEE);
        if (applyFee.getValue() === ApplyFee.WAIVE) {
            this.shouldResetFieldsForWaive(applyFeeChanged).subscribe((shouldResetFieldsForWaive: boolean) => {
                if (shouldResetFieldsForWaive) {
                    if (applyFeeChanged) {
                        this.formMgr.get(FeeFormula.FEES_SUSP_ACC_NO).markAsUntouched().setValue('');;
                        this.formMgr.get(FeeFormula.FEES_SUSP_ACC_CCY).markAsUntouched().setValue('');
                        this.formMgr.get(FeeFormula.FEES_SUSP_ACC_OFFICE).markAsUntouched().setValue('');
                        this.formMgr.get(FeeFormula.METHOD).setValue(FeeMethod.REGULAR);
                        this.formMgr.get(FeeFormula.TIER_TYPE).markAsUntouched().setValue('');;
                        this.formMgr.get(FeeFormula.PERCENTAGE_FEE).markAsUntouched().setValue('');
                        this.formMgr.get(FeeFormula.MIN_FEE).markAsUntouched().setValue('');
                        this.formMgr.get(FeeFormula.MAX_FEE).markAsUntouched().setValue('');
                        this.formMgr.get(FeeFormula.DETAILS).reset();//reset or set value []?
                    }
                    this.formMgr.get(FeeFormula.FEES_SUSP_ACC_NO).disable();
                    this.formMgr.get(FeeFormula.METHOD).disable();
                    this.formMgr.get(FeeFormula.TIER_TYPE).disable().markAsOptional();
                    this.formMgr.get(FeeFormula.FIXED_FEE).setValue(0).disable();
                    this.formMgr.get(FeeFormula.PERCENTAGE_FEE).disable();
                    this.formMgr.get(FeeFormula.MIN_FEE).disable();
                    this.formMgr.get(FeeFormula.MAX_FEE).disable();
                    this.formMgr.get(FeeFormula.DETAILS).disable().markAsOptional();
                    this.formMgr.get(FeeFormula.DETAILS).getLayoutControl<MultiLineComponent>().controlData.minItems = 0;
                    this.applyFeeLastValue = ApplyFee.WAIVE;
                } else {
                    applyFee.setValue(this.applyFeeLastValue)
                }
            });
        } else {
            if (applyFeeChanged) {
                const isAgentFee = this.formMgr.get(FeeFormula.V_FEE_PARTYTYPE).getValue() === PayingPartyType.AGENT_FEE;
                if (!isAgentFee) {
                    this.formMgr.get(FeeFormula.FEES_SUSP_ACC_NO).enable();
                }
                this.formMgr.get(FeeFormula.METHOD).enable();
                if (this.applyFeeLastValue === ApplyFee.WAIVE) {
                    this.formMgr.get(FeeFormula.FIXED_FEE).markAsUntouched().setValue('').enable();
                    this.formMgr.get(FeeFormula.PERCENTAGE_FEE).enable();
                    this.formMgr.get(FeeFormula.MIN_FEE).enable();
                    this.formMgr.get(FeeFormula.MAX_FEE).enable();
                }
            }
            this.applyFeeLastValue = applyFee.getValue();
        }
    }

    private shouldResetFieldsForWaive(applyFeeChanged: boolean): Observable<boolean> {
        return applyFeeChanged ?
            this.openConfirmModal(ConfirmModalType.ApplyFeeChange).pipe(map((response: BFEvents.DialogEvent) => response.value.button === 'ok')) :
            of(true);
    }

    private handleFieldsByAccountNumber(): void {
        const data = this.getDrilldownData(FeeFormula.FEES_SUSP_ACC_NO);
        const currency = data ? data[Accounts.CURRENCY] : '';
        const office = data ? data[Accounts.OFFICE] : '';
        this.formMgr.get(FeeFormula.FEES_SUSP_ACC_CCY).setValue(currency);
        this.formMgr.get(FeeFormula.FEES_SUSP_ACC_OFFICE).setValue(office);
    }

    private handleFieldsByFeeMethod(feeMethodChanged: boolean): void {
        const method = this.formMgr.get(FeeFormula.METHOD);
        const tierType = this.formMgr.get(FeeFormula.TIER_TYPE);
        const tiers = this.formMgr.get(FeeFormula.DETAILS);
        const fixedAmount = this.formMgr.get(FeeFormula.FIXED_FEE);
        const percentage = this.formMgr.get(FeeFormula.PERCENTAGE_FEE);

        if (method.getValue() === FeeMethod.TIERED) {
            if (this.layoutMode !== ProfileLayoutType.VIEW) {
                tierType.enable();
                tiers.enable();
            }
            tierType.markAsRequired();
            tiers.markAsRequired();
            tiers.getLayoutControl<MultiLineComponent>().controlData.minItems = 1;
            if (feeMethodChanged) {
                fixedAmount.markAsUntouched().setValue('');
                percentage.markAsUntouched().setValue('');
            }
            fixedAmount.disable();
            percentage.disable();
        } else if (this.formMgr.get(FeeFormula.APPLY_FEE).getValue() !== ApplyFee.WAIVE) {
            this.shouldResetTiers(feeMethodChanged, method.getValue()).subscribe((shouldResetTiers: boolean) => {
                if (shouldResetTiers) {
                    if (feeMethodChanged) {
                        tierType.markAsUntouched().setValue('');
                        tiers.reset();////reset or set value []?
                    }
                    tierType.disable().markAsOptional();
                    tiers.disable().markAsOptional();
                    tiers.getLayoutControl<MultiLineComponent>().controlData.minItems = 0;
                    if (this.layoutMode !== ProfileLayoutType.VIEW) {
                        fixedAmount.enable();
                        percentage.enable();
                    }
                } else {
                    method.setValue(FeeMethod.TIERED)
                }
            });
        }
    }

    private shouldResetTiers(feeMethodChanged: boolean, method: FeeMethod): Observable<boolean> {
        const tiers = this.formMgr.get(FeeFormula.DETAILS);
        return feeMethodChanged && tiers.getValue()?.length ?
            this.openConfirmModal(ConfirmModalType.DeleteTiers, { method }).pipe(map((response: BFEvents.DialogEvent) => response.value.button === 'ok')) :
            of(true);
    }

    private handleFieldsByFixedAmount(fixedAmountChanged: boolean): void {
        const fixedAmount = this.formMgr.get(FeeFormula.FIXED_FEE);
        const percentage = this.formMgr.get(FeeFormula.PERCENTAGE_FEE);
        const minFee = this.formMgr.get(FeeFormula.MIN_FEE);
        const maxFee = this.formMgr.get(FeeFormula.MAX_FEE);
        if (fixedAmount.getValue() || fixedAmount.getValue() === 0) {
            if (fixedAmountChanged) {
                percentage.markAsUntouched().setValue('');
                minFee.markAsUntouched().setValue('');
                maxFee.markAsUntouched().setValue('');
            }
            percentage.disable();
            minFee.disable();
            maxFee.disable();
        } else if (fixedAmountChanged) {
            if (this.formMgr.get(FeeFormula.METHOD).getValue() !== FeeMethod.TIERED) {
                percentage.enable();
            }
            minFee.enable();
            maxFee.enable();
        }
    }

    private handleFieldsByPercentage(percentageChanged: boolean): void {
        const percentage = this.formMgr.get(FeeFormula.PERCENTAGE_FEE);
        const fixedAmount = this.formMgr.get(FeeFormula.FIXED_FEE);
        if (percentage.getValue() || percentage.getValue() === 0) {
            if (percentageChanged) {
                fixedAmount.markAsUntouched().setValue('');
            }
            fixedAmount.disable();
        } else if (percentageChanged && this.formMgr.get(FeeFormula.METHOD).getValue() !== FeeMethod.TIERED && this.formMgr.get(FeeFormula.APPLY_FEE).getValue() !== ApplyFee.WAIVE) {
            fixedAmount.enable();
        }
    }

    private updateMaxFeeValidation(): void {
        const minFeeControl = this.formMgr.get(FeeFormula.MIN_FEE);
        const maxFeeControl = this.formMgr.get(FeeFormula.MAX_FEE);
        const minFee = minFeeControl.getValue();
        const maxFeeMinimumValidation = minFee || minFee === 0 ? minFee + 0.01 : 0;
        maxFeeControl.getLayoutControl().set('minimum', maxFeeMinimumValidation);
    }

    private openConfirmModal(type: ConfirmModalType, translateParams?: any): Observable<BFEvents.DialogEvent> {
        const layout = structuredClone(FeeFormulaConfirmModalLayout);
        layout.properties.controls.message.title = this.translate.instant(`business-framework.profiles.415.modals.${type}.body`, translateParams);

        const modalConfig = <IDynamicModalDialogData>{
            size: ModalSize.SMALL,
            layout: layout,
            contract: FeeFormulaConfirmModalContract,
            title: this.translate.instant(`business-framework.profiles.415.modals.${type}.title`),
            okButtonTitle: `business-framework.profiles.415.modals.${type}.buttons.ok`,
            cancelButtonTitle: `business-framework.profiles.415.modals.${type}.buttons.cancel`
        };
        return this.openModal(modalConfig).pipe((take(1)));
    }

    private validateMandatoryFixedAmountOrPercentage(): boolean {
        const method = this.formMgr.get(FeeFormula.METHOD);
        const fixedAmount = this.formMgr.get(FeeFormula.FIXED_FEE);
        const percentage = this.formMgr.get(FeeFormula.PERCENTAGE_FEE);
        if (method.getValue() === FeeMethod.REGULAR && !fixedAmount.getValue() && fixedAmount.getValue() !== 0 && !percentage.getValue() && percentage.getValue() !== 0) {
            this.notificationService.error(this.translate.instant('business-framework.profiles.415.errors.mandatory-fixed-amount-or-percentage'));
            return false;
        }
        return true;
    }
}
